import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, SEOHead } from '../components';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';
import { 
  ShoppingBagIcon,
  TagIcon,
  PresentationChartLineIcon,
  BeakerIcon,
  ArrowRightIcon,
  CheckIcon,
  StarIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

const AmazonAdsPage: React.FC = () => {
  const navigate = useNavigate();
  useSEO(pageConfigs.amazonAds);

  // Navigation handlers
  const handleGetAudit = () => {
    navigate('/consultation');
  };

  const handleViewCaseStudies = () => {
    navigate('/case-studies');
  };

  const handleScheduleConsultation = () => {
    navigate('/consultation');
  };

  const handleGetStarted = () => {
    navigate('/quote');
  };

  const handleLearnMore = () => {
    navigate('/contact');
  };
  const features = [
    {
      title: "Sponsored Products",
      description: "Strategic management of Sponsored Products campaigns to maximize visibility and sales.",
      icon: <ShoppingBagIcon className="w-8 h-8" />,
      benefits: ["Keyword research", "Bid optimization", "Campaign structure", "Performance tracking"]
    },
    {
      title: "Sponsored Brands",
      description: "Comprehensive management of Sponsored Brands to build brand awareness and drive sales.",
      icon: <TagIcon className="w-8 h-8" />,
      benefits: ["Custom headlines", "Logo optimization", "Product selection", "Landing page optimization"]
    },
    {
      title: "Amazon DSP",
      description: "Strategic implementation of Amazon's Demand-Side Platform for programmatic advertising.",
      icon: <PresentationChartLineIcon className="w-8 h-8" />,
      benefits: ["Audience targeting", "Retargeting", "Cross-channel campaigns", "Performance analysis"]
    },
    {
      title: "Product Optimization",
      description: "Complete optimization of your product listings to maximize conversion rates and organic ranking.",
      icon: <BeakerIcon className="w-8 h-8" />,
      benefits: ["Title optimization", "Bullet points", "Description enhancement", "Image optimization"]
    }
  ];

  const process = [
    {
      step: "01",
      title: "Account Analysis",
      description: "We analyze your current Amazon advertising performance, account structure, and historical data."
    },
    {
      step: "02",
      title: "Strategy Development",
      description: "Create a comprehensive Amazon advertising strategy tailored to your business goals."
    },
    {
      step: "03",
      title: "Implementation",
      description: "Set up campaigns, optimize product listings, and implement proper tracking."
    },
    {
      step: "04",
      title: "Optimization & Scaling",
      description: "Continuously monitor, test, and optimize your Amazon presence to maximize sales and ROAS."
    }
  ];

  const caseStudy = {
    client: "Consumer Electronics Brand",
    industry: "Electronics",
    challenge: "Low visibility and sales on Amazon despite quality products",
    solution: "Implemented comprehensive Amazon advertising strategy and product listing optimization",
    results: [
      "500% increase in Amazon sales",
      "320% improvement in ROAS",
      "75% reduction in ACoS",
      "Top 3 ranking in all target categories"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.amazonAds} />
      <Header />

      {/* Hero Section */}
      <section className="pt-40 section-padding bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-blue-100 rounded-full px-4 py-2 mb-6">
                <ShoppingBagIcon className="w-5 h-5 text-blue-600" />
                <span className="text-blue-700 font-semibold text-sm">Amazon Advertising Experts</span>
              </div>
              
              <h1 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6 leading-tight">
                Strategic 
                <span className="text-blue-600"> Amazon Advertising</span>
              </h1>
              
              <p className="text-xl text-slate-700 mb-8 leading-relaxed">
                Dominate the Amazon marketplace with strategic advertising and product optimization.
                Our data-driven approach maximizes your sales and ROI.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <button 
                  onClick={handleGetAudit}
                  className="bg-slate-900 hover:bg-slate-800 text-white font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <span>Get Amazon Ads Audit</span>
                  <ArrowRightIcon className="w-5 h-5" />
                </button>
                <button 
                  onClick={handleViewCaseStudies}
                  className="bg-white hover:bg-gray-50 text-slate-900 font-semibold py-4 px-8 rounded-lg border-2 border-slate-200 transition-colors"
                >
                  View Case Studies
                </button>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">500%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Sales Increase</div>
                  <div className="text-xs text-slate-500">Average for clients</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">320%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">ROAS Improvement</div>
                  <div className="text-xs text-slate-500">Return on ad spend</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">75%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">ACoS Reduction</div>
                  <div className="text-xs text-slate-500">Advertising cost of sale</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">Top 3</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Category Ranking</div>
                  <div className="text-xs text-slate-500">Target categories</div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                <div className="flex items-center space-x-3 mb-6">
                  <ShoppingBagIcon className="w-6 h-6 text-blue-600" />
                  <h3 className="text-2xl font-display font-bold text-slate-900">Free Amazon Ads Audit</h3>
                </div>
                <p className="text-slate-600 mb-6">
                  Get a comprehensive analysis of your current Amazon advertising performance and 
                  receive a customized strategy to improve your results.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Campaign structure review</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Product listing analysis</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Custom optimization recommendations</span>
                  </div>
                </div>
                
                <button 
                  onClick={handleGetAudit}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  Get Your Free Audit
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-display font-bold text-slate-900 mb-4">
              Comprehensive Amazon Advertising Services
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our strategic approach maximizes your visibility and sales on the Amazon marketplace
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-slate-100">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <div className="text-blue-600">{feature.icon}</div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-display font-bold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {feature.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckIcon className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-display font-bold text-slate-900 mb-4">
              Our 4-Step Process
            </h2>
            <p className="text-xl text-slate-600">
              Proven methodology for Amazon advertising success
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">{step.step}</span>
                </div>
                <h3 className="text-xl font-display font-bold text-slate-900 mb-4">{step.title}</h3>
                <p className="text-slate-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="bg-slate-50 rounded-2xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center space-x-2 bg-green-100 rounded-full px-4 py-2 mb-6">
                  <StarIcon className="w-5 h-5 text-green-600" />
                  <span className="text-green-700 font-semibold text-sm">Success Story</span>
                </div>
                
                <h2 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6">
                  {caseStudy.client}
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Challenge</h4>
                    <p className="text-slate-600">{caseStudy.challenge}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Solution</h4>
                    <p className="text-slate-600">{caseStudy.solution}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Results</h4>
                    <div className="space-y-2">
                      {caseStudy.results.map((result, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <CheckIcon className="w-5 h-5 text-green-600" />
                          <span className="text-slate-700">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
                <div className="text-center mb-6">
                  <div className="text-5xl mb-4">🛒</div>
                  <h3 className="text-2xl font-display font-bold text-slate-900 mb-2">Ready to dominate Amazon?</h3>
                  <p className="text-slate-600">Schedule a consultation with our Amazon advertising experts</p>
                </div>
                
                <button 
                  onClick={handleScheduleConsultation}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors mb-4"
                >
                  Schedule Consultation
                </button>
                
                <button 
                  onClick={handleViewCaseStudies}
                  className="w-full bg-white hover:bg-gray-50 text-slate-900 font-semibold py-3 px-6 rounded-lg border-2 border-slate-200 transition-colors"
                >
                  View More Case Studies
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Amazon Ad Types Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-display font-bold text-slate-900 mb-4">
              Amazon Ad Types We Manage
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Comprehensive management across all Amazon advertising platforms
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">🛍️</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Sponsored Products</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">🏷️</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Sponsored Brands</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">📺</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Sponsored Display</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Amazon DSP</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">📱</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Stores</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">🔍</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Product Targeting</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-display font-bold text-slate-900 mb-4">
              Key Metrics We Optimize
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our data-driven approach focuses on the metrics that matter most
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">📉</div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-2">ACoS</h3>
              <p className="text-slate-600">Advertising Cost of Sale</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">📈</div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-2">ROAS</h3>
              <p className="text-slate-600">Return on Ad Spend</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-2">CTR</h3>
              <p className="text-slate-600">Click-Through Rate</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">💰</div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-2">CPC</h3>
              <p className="text-slate-600">Cost Per Click</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">🛒</div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-2">Conversion Rate</h3>
              <p className="text-slate-600">Product Page to Purchase</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-2">Organic Ranking</h3>
              <p className="text-slate-600">Category Position</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl lg:text-3xl font-display font-bold mb-6">
              Ready to Dominate the Amazon Marketplace?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Get started with a free Amazon advertising audit and custom strategy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleGetStarted}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors"
              >
                Get Started
              </button>
              <button 
                onClick={handleLearnMore}
                className="bg-transparent hover:bg-white/10 text-white font-semibold py-4 px-8 rounded-lg border-2 border-white/20 transition-colors"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AmazonAdsPage;